"use client"

import { useState, useEffect } from "react"
import type { CSSProperties } from "react"
import { Flame, Award, Hash } from "lucide-react"
import {
  fetchHotTags,
  fetchRankingPosts,
  fetchRecommendedCreators,
} from "@/lib/community/community-api"
import type { CommunityPost, CommunityUser } from "@zrt-ai-office/types"
import { AbilityTypeBadge } from "./ability-type-badge"
import { formatCount } from "@/lib/community/utils"
import { AuthorLink } from "./author-link"
import { COMMUNITY_SIDEBAR_ADS, type CommunitySidebarAd } from "./community-config"

const AD_ROTATION_INTERVAL = 5000

/**
 * CommunityRightSidebar — sticky aside with:
 * - Hot posts leaderboard (from ranking API)
 * - Trending tags (clickable for filtering)
 * - Active creators (from ranking API)
 */
export function CommunityRightSidebar({
  activeTag,
  onTagClick,
}: {
  activeTag: string | null
  onTagClick: (tag: string | null) => void
}) {
  const [hotPosts, setHotPosts] = useState<CommunityPost[]>([])
  const [topCreators, setTopCreators] = useState<CommunityUser[]>([])
  const [trendingTags, setTrendingTags] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [activeAdIndex, setActiveAdIndex] = useState(0)
  const [isAdHovered, setIsAdHovered] = useState(false)

  useEffect(() => {
    setLoading(true)
    Promise.all([
      fetchRankingPosts(),
      fetchRecommendedCreators(),
      fetchHotTags(),
    ])
      .then(([posts, creators, tagsRes]) => {
        setHotPosts(posts)
        setTopCreators(creators)
        setTrendingTags(tagsRes.slice(0, 12).map((t) => t.name))
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (COMMUNITY_SIDEBAR_ADS.length <= 1 || isAdHovered) return

    const timer = window.setInterval(() => {
      setActiveAdIndex((index) => (index + 1) % COMMUNITY_SIDEBAR_ADS.length)
    }, AD_ROTATION_INTERVAL)

    return () => window.clearInterval(timer)
  }, [isAdHovered])

  const sectionCardStyle = {
    background: 'color-mix(in oklch, oklch(var(--card)) 80%, transparent)',
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    border: '1px solid color-mix(in oklch, oklch(var(--border)) 40%, transparent)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.03)',
  } as CSSProperties
  const hasSidebarAds = COMMUNITY_SIDEBAR_ADS.length > 0

  return (
    <div className="flex flex-col space-y-4">
      {/* ── Hot Posts Leaderboard ── */}
      <section className="rounded-2xl p-4" style={sectionCardStyle}>
        <h3 className="flex items-center gap-1.5 text-xs font-bold text-foreground mb-3">
          <div className="flex items-center justify-center w-5 h-5 rounded-md bg-orange-500/10">
            <Flame className="h-3 w-3 text-orange-500" />
          </div>
          热门内容
        </h3>
        {loading ? (
          <div className="space-y-2.5">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-start gap-2">
                <div className="h-3.5 w-3 rounded bg-foreground/8 animate-pulse shrink-0 mt-0.5" />
                <div className="flex-1 space-y-1">
                  <div className="h-3 w-full rounded bg-foreground/8 animate-pulse" />
                  <div className="h-2.5 w-2/3 rounded bg-foreground/8 animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : hotPosts.length === 0 ? (
          <div className="flex flex-col items-center py-6 text-center">
            <Flame className="h-5 w-5 text-muted-foreground/20 mb-2" />
            <p className="text-micro text-muted-foreground/60 mb-3">暂无热门内容</p>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event("safeclaw:open-publish"))}
              className="px-3 py-1.5 rounded-full text-xs font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
            >
              去发布
            </button>
          </div>
        ) : (
        <div className="space-y-2">
          {hotPosts.slice(0, 5).map((post, i) => (
            <button
              key={post.id}
              type="button"
              onClick={() => window.open(`${import.meta.env.BASE_URL}community/post/${post.id}`, "_blank")}
              className="flex items-start gap-2 group w-full text-left rounded-lg p-1.5 -mx-1.5 hover:bg-foreground/[0.03] transition-colors"
            >
              <span
                className={`text-xs font-bold tabular-nums shrink-0 mt-0.5 w-4 text-center ${
                  i < 3 ? "text-orange-500" : "text-muted-foreground/40"
                }`}
              >
                {i + 1}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-foreground/80 line-clamp-2 group-hover:text-foreground transition-colors cursor-pointer leading-relaxed">
                  {post.title}
                </p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <AbilityTypeBadge type={post.abilityType} size="sm" />
                  <span className="text-micro text-muted-foreground/60">
                    {formatCount(post.stats.likes)} 赞
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
        )}
      </section>

      {hasSidebarAds && (
        <SidebarAdCarousel
          ads={COMMUNITY_SIDEBAR_ADS}
          activeIndex={activeAdIndex}
          onSelect={setActiveAdIndex}
          onHoverChange={setIsAdHovered}
          style={sectionCardStyle}
        />
      )}

      {/* ── Trending Tags ── */}
      <section className="rounded-2xl p-4" style={sectionCardStyle}>
        <h3 className="flex items-center gap-1.5 text-xs font-bold text-foreground mb-3">
          <div className="flex items-center justify-center w-5 h-5 rounded-md bg-primary/10">
            <Hash className="h-3 w-3 text-primary" />
          </div>
          热门标签
        </h3>
        {loading ? (
          <div className="flex flex-wrap gap-1.5">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-6 rounded-full bg-foreground/8 animate-pulse" style={{ width: `${40 + (i % 3) * 16}px` }} />
            ))}
          </div>
        ) : trendingTags.length === 0 ? (
          <p className="text-micro text-muted-foreground/60 py-2">暂无热门标签</p>
        ) : (
        <div className="flex flex-col gap-0.5">
          {activeTag && (
            <button
              type="button"
              onClick={() => onTagClick(null)}
              className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium text-red-500/80 hover:text-red-500 hover:bg-red-500/5 transition-colors"
            >
              ✕ 清除筛选
            </button>
          )}
          {trendingTags.slice(0, 8).map((tag) => (
            <button
              key={tag}
              type="button"
              onClick={() => onTagClick(activeTag === tag ? null : tag)}
              className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs transition-all duration-150 text-left ${
                activeTag === tag
                  ? "bg-primary/10 text-primary font-medium"
                  : "text-foreground/70 hover:text-foreground hover:bg-foreground/[0.03]"
              }`}
            >
              <Hash className="h-3 w-3 shrink-0 text-muted-foreground/40" />
              {tag}
            </button>
          ))}
        </div>
        )}
      </section>

      {/* ── Active Creators ── */}
      <section className="rounded-2xl p-4" style={sectionCardStyle}>
        <h3 className="flex items-center gap-1.5 text-xs font-bold text-foreground mb-3">
          <div className="flex items-center justify-center w-5 h-5 rounded-md bg-violet-500/10">
            <Award className="h-3 w-3 text-violet-500" />
          </div>
          活跃创作者
        </h3>
        {loading ? (
          <div className="space-y-2.5">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="h-7 w-7 rounded-full bg-foreground/8 animate-pulse shrink-0" />
                <div className="flex-1 space-y-1">
                  <div className="h-3 w-20 rounded bg-foreground/8 animate-pulse" />
                  <div className="h-2.5 w-28 rounded bg-foreground/8 animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : topCreators.length === 0 ? (
          <p className="text-micro text-muted-foreground/60 py-2">暂无活跃创作者</p>
        ) : (
        <div className="space-y-2">
          {topCreators.map((creator) => (
            <div key={creator.id} className="flex items-center gap-2 rounded-lg p-1 -mx-1 hover:bg-foreground/[0.03] transition-colors">
              <AuthorLink
                authorId={creator.id}
                avatar={creator.avatar}
                displayName={creator.displayName}
              />
            </div>
          ))}
        </div>
        )}
      </section>
    </div>
  )
}

function SidebarAdCarousel({
  ads,
  activeIndex,
  onSelect,
  onHoverChange,
  style,
}: {
  ads: CommunitySidebarAd[]
  activeIndex: number
  onSelect: (index: number) => void
  onHoverChange: (isHovered: boolean) => void
  style: CSSProperties
}) {
  if (ads.length === 0) return null

  const normalizedActiveIndex = activeIndex % ads.length
  const ad = ads[normalizedActiveIndex]!

  return (
    <section className="rounded-2xl overflow-hidden" style={style}>
      <a
        href={ad.linkUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={ad.alt || "社区广告"}
        className="block group"
        onMouseEnter={() => onHoverChange(true)}
        onMouseLeave={() => onHoverChange(false)}
        onFocus={() => onHoverChange(true)}
        onBlur={() => onHoverChange(false)}
      >
        <div className="relative aspect-[16/9] overflow-hidden bg-foreground/5">
          <img
            src={ad.imageUrl}
            alt={ad.alt || "社区广告"}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            loading="lazy"
          />
          <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/35 to-transparent opacity-80" />

          {ads.length > 1 && (
            <div className="absolute inset-x-0 bottom-2 flex items-center justify-center gap-1.5">
              {ads.map((adItem, index) => (
                <button
                  key={adItem.id}
                  type="button"
                  aria-label={`切换到第 ${index + 1} 个广告`}
                  aria-current={index === normalizedActiveIndex ? "true" : undefined}
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    onSelect(index)
                  }}
                  className={`h-1.5 rounded-full transition-all ${
                    index === normalizedActiveIndex
                      ? "w-4 bg-white"
                      : "w-1.5 bg-white/45 hover:bg-white/80"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </a>
    </section>
  )
}
